import { Dispatch, SetStateAction } from 'react';

import { AssessmentDisplayStatus, getAgeFromDob } from '@equip.health/ui';
import { isEmptyArray, isObject } from 'formik';
import { jsPDF as JSPDF } from 'jspdf';
import { cloneDeep, intersection, isEmpty, round } from 'lodash';
import { DateTime } from 'luxon';

import { Model } from 'survey-core';

import logo from '~/assets/img/equipText.png';
import {
  diagnosisClinicianSurveyNames,
  generalClinicianSurveyNames,
} from '~/lib/constants/survey/CategorizedSurveyNames';
import { SurveyKeys } from '~/lib/constants/survey/SurveyKeys';
import { SurveyName } from '~/lib/constants/survey/SurveyName';
import { SurveyNameToAlertQuestionsMap } from '~/lib/constants/survey/SurveyNameToAlertQuestionsMap';
import { SurveyDiagnosesToDiagnosesQuestionKeysMap } from '~/lib/constants/survey/SurveyNameToDiagnosesQuestionKeysMap';
import { SurveyNameToDisplayNameMap } from '~/lib/constants/survey/SurveyNameToDisplayNameMap';
import { SurveyNameToSurveyKeyMap } from '~/lib/constants/survey/SurveyNameToSurveyKeyMap';
import { SurveyResponseStatus } from '~/lib/constants/survey/SurveyResponseStatus';
import { SurveyResponseStatusToDisplayStatusMap } from '~/lib/constants/survey/SurveyResponseStatusToDisplayStatusMap';
import { SurveysToCheckForAlert } from '~/lib/constants/survey/SurveysToCheckForAlert';
import { SurveysWithScore } from '~/lib/constants/survey/SurveysWithScore';
import { SurveyInstanceData } from '~/lib/types/assessments/SurveyInstanceData';
import { AlertSurveys } from '~/lib/types/survey/AlertSurveys';

import { DiagnosisStatusCode, isOpenDiagnosis } from './clinicalProfile.util';
import util from './util';
import { removeUrlsFromText } from '../utils';

enum Splitters {
  AND = ' and ',
  Dropdown = ' <> ',
  GreaterAndEqualTo = ' >= ',
  GreaterThan = ' > ',
  LesserAndEqualTo = ' <= ',
  LesserThan = ' < ',
  MultiSelectDropdown = ' anyof ',
  NotEmpty = ' notempty',
  OR = ' or ',
  Toggle = ' = ',
}

const getCompletedByUserName = (assessment: SurveyResponse): string => {
  const isSubmittedUserDisplayNameAvailable =
    assessment.submittedUserDisplayFirstName &&
    assessment.submittedUserDisplayLastName;

  return isSubmittedUserDisplayNameAvailable
    ? `${assessment.submittedUserDisplayFirstName} ${assessment.submittedUserDisplayLastName}`
    : assessment.submittedUserName || '';
};

const fontFace = 'helvetica';
const fontSize = 12;
const margin = 16;
const pageHeight = 297; // standard height of a4 document in millimeters
const pageWidth = 210; // standard width of a4 document in millimeters
const ptsPerInch = 12;

const pdfOffsets = {
  answer: { x: 4, y: 10 },
  completed: { x: 0, y: 35 },
  logo: { x: 0, y: 0 },
  patientName: { x: 0, y: 25 },
  resultsContainer: { x: 0, y: 70 },
  title: { x: 0, y: 15 },
};

const QuestionsKeysToExcludeOnScoreCalculation: Record<string, string[]> = {
  [SurveyName.DUDIT]: [`${SurveyKeys.DUDIT}c_score`],
  [SurveyName.AUDIT]: [
    `${SurveyKeys.AUDIT_KEY}pt_sex`,
    `${SurveyKeys.AUDIT_KEY}c_score`,
  ],
};

const ClinicianAssessmentsSurveyCards: string[] = [
  ...diagnosisClinicianSurveyNames,
  ...generalClinicianSurveyNames,
];

const EdeQSurveyScoreKeys: string[] = [
  `${SurveyKeys.EDE_Q_KEY}1`,
  `${SurveyKeys.EDE_Q_KEY}2`,
  `${SurveyKeys.EDE_Q_KEY}3`,
  `${SurveyKeys.EDE_Q_KEY}4`,
  `${SurveyKeys.EDE_Q_KEY}5`,
  `${SurveyKeys.EDE_Q_KEY}6`,
  `${SurveyKeys.EDE_Q_KEY}7`,
  `${SurveyKeys.EDE_Q_KEY}8`,
  `${SurveyKeys.EDE_Q_KEY}8`,
  `${SurveyKeys.EDE_Q_KEY}9`,
  `${SurveyKeys.EDE_Q_KEY}10`,
  `${SurveyKeys.EDE_Q_KEY}11`,
  `${SurveyKeys.EDE_Q_KEY}12`,
  `${SurveyKeys.EDE_Q_KEY}19`,
  `${SurveyKeys.EDE_Q_KEY}20`,
  `${SurveyKeys.EDE_Q_KEY}21`,
  `${SurveyKeys.EDE_Q_KEY}22`,
  `${SurveyKeys.EDE_Q_KEY}23`,
  `${SurveyKeys.EDE_Q_KEY}24`,
  `${SurveyKeys.EDE_Q_KEY}25`,
  `${SurveyKeys.EDE_Q_KEY}26`,
  `${SurveyKeys.EDE_Q_KEY}27`,
  `${SurveyKeys.EDE_Q_KEY}28`,
];

/** Compile all questions by iterating through page elements - some pages have elements nested within elements */
const getAllQuestions = (definition: SurveyDefinition): SurveyQuestion[] =>
  (definition?.pages ?? []).flatMap((page: SurveyPage) =>
    page.elements.flatMap((element: SurveyQuestion) =>
      element.elements?.length ? element.elements : [element],
    ),
  );

const getQuestion = (
  questions: SurveyQuestion[],
  questionName: string,
): SurveyQuestion =>
  questions.find(({ name }: SurveyQuestion) => name === questionName);

const getSurveyAnswerOption = (
  options: SurveyAnswerOption[],
  answerValue: string,
): SurveyAnswerOption =>
  (options ?? []).find(
    ({ value }: SurveyAnswerOption) => value === answerValue,
  );

const isBoolOrStringOrNumber = (value: any): boolean =>
  ['boolean', 'string', 'number'].includes(typeof value);

/** Parse an object type and return its value converted to string */
const parseObject = (obj: unknown): string =>
  Object.values(obj)
    .map((value) => {
      if (isBoolOrStringOrNumber(value)) return value.toString();
      if (Array.isArray(value)) return value;
      return '';
    })
    .join(' - ');

/** Parse a value that can be of type string, string[] ,object, or any[] into a string[] */
const parseAnswerValueToStringArray = (
  answerValue: string | object | string[] | unknown[],
): string[] => {
  if (isBoolOrStringOrNumber(answerValue)) {
    return [answerValue.toString()];
  }

  if (Array.isArray(answerValue)) {
    return answerValue.map((answerValuePart) => {
      if (isBoolOrStringOrNumber(answerValuePart)) {
        return answerValuePart.toString();
      }

      if (Object.keys(answerValuePart ?? {}).length) {
        return parseObject(answerValuePart);
      }

      return '';
    });
  }

  if (isObject(answerValue)) {
    return [parseObject(answerValue)];
  }

  return [];
};

/**
 * Formats a US 10-digit telephone number as "(XXX) XXX-XXXX"
 * @function parseTelephone
 * @param {string} tel A telephone number containing exactly 10 digits
 * @return {string}
 */
const parseTelephone = (tel: string): string => {
  if (tel?.length !== 10) return tel;
  return `(${tel.slice(0, 3)}) ${tel.slice(3, 6)}-${tel.slice(6)}`;
};

const parseInputTypeMapper: Record<'date' | 'tel', (text: string) => string> = {
  date: (text: string): string =>
    DateTime.fromFormat(text, 'yyyy-LL-dd').toFormat('LL/dd/yyyy'),
  tel: parseTelephone,
};

const parseText = (inputType: string, text: string[]): string[] =>
  text.map(
    (textPart: string): string =>
      parseInputTypeMapper[inputType]?.(textPart) ?? textPart,
  );

const getAnswerTitle = (
  questions: SurveyQuestion[],
  questionName: string,
  answerValue: any,
  answerValueComment?: any,
): string[] => {
  const {
    choices,
    inputType,
    showOtherItem,
    otherText,
    showNoneItem,
    noneText,
    labelTrue,
    labelFalse,
  } = getQuestion(questions, questionName) ?? {};

  const result: string[] = parseAnswerValueToStringArray(answerValue);

  const parsedText = parseText(inputType, result);

  if (answerValue === true && labelTrue) {
    return [labelTrue];
  }
  if (answerValue === false && labelFalse) {
    return [labelFalse];
  }

  if (!choices?.length) return parsedText;

  const answer = parsedText.map((answerValuePart: string) =>
    (
      getSurveyAnswerOption(choices, answerValuePart)?.text ?? answerValuePart
    ).toString(),
  );
  let otherPrefix = 'Other';
  if (showOtherItem) {
    otherPrefix = otherText;
  }
  if (answerValueComment && !util.isEmptyArray(answerValueComment)) {
    if (Array.isArray(answerValueComment)) {
      answer.push(
        ...answerValueComment.map(
          (commentValue) => `${otherPrefix} - ${commentValue}`,
        ),
      );
    } else if (typeof answerValueComment === 'string') {
      answer.push(`${otherPrefix} - ${answerValueComment}`);
    }
    const index = answer.indexOf('other');
    if (index > -1) {
      answer.splice(index, 1);
    }
  }

  if (showNoneItem && noneText) {
    if (Array.isArray(answerValue) && answerValue.includes('none'))
      answer.push(noneText);
    else if (typeof answerValue === 'string' && answerValue === 'none')
      answer.push(noneText);

    const noneIndex = answer.indexOf('none');

    if (noneIndex > -1) answer.splice(noneIndex, 1);
  }

  return answer;
};

/** Helper to parse boolean value to readable 'Yes' or 'No' **/

const parseBooleanText = (answer: string): string => {
  if (answer === 'true') {
    return 'Yes';
  }
  if (answer === 'false') {
    return 'No';
  }
  return answer;
};

const getExcludedQuestionKeys = (surveyName: string): string[] => {
  if (surveyName === SurveyName.SAFETY_PLAN)
    return [`${SurveyKeys.SAFETY_PLAN_KEY}risk_level`];
  return [];
};

/** Helper to calculate the total score of a survey.
  Score is calculated by adding the selected values of all
  questions */
const sumSurveyQuestions = (
  jsonResults: string,
  key: string,
  keysToExclude: string[] = [],
): number => {
  const parsedJsonRes = util.parseJson(jsonResults);
  let totalScore = null;
  Object.keys(parsedJsonRes).forEach((eachKey) => {
    const score = Number(parsedJsonRes[eachKey]);
    if (
      eachKey.startsWith(key) &&
      !Number.isNaN(score) &&
      !keysToExclude.includes(eachKey)
    ) {
      totalScore += score;
    }
  });
  return totalScore;
};

/**
 * Helper to calculate the sum of a given set of questions
 * Has specialized logic for UI v1
 * @deprecated
 */
const sumSpecificKeysLegacy = (
  jsonResults: string,
  questionKeys: string[],
): number => {
  const parsedJsonRes = util.parseJson(jsonResults);
  let totalScore = 0;
  Object.keys(parsedJsonRes).forEach((_key) => {
    if (questionKeys.includes(_key)) {
      if (
        [
          'aims_1',
          'aims_2',
          'aims_3',
          'aims_4',
          'aims_5',
          'aims_6',
          'aims_7',
        ].includes(_key)
      ) {
        totalScore += util.parseNumberCoalescingNaN(
          parsedJsonRes[_key][0].Code,
        );
      } else {
        totalScore += util.parseNumberCoalescingNaN(parsedJsonRes[_key]);
      }
    }
  });
  return totalScore;
};

/**
 * Helper to calculate the sum of a given set of questions
 */
const sumSpecificKeys = (
  jsonResults: string,
  questionKeys: string[],
): number => {
  const parsedJsonRes = util.parseJson(jsonResults);
  let totalScore = 0;
  Object.keys(parsedJsonRes).forEach((_key) => {
    if (questionKeys.includes(_key)) {
      totalScore += Number(parsedJsonRes[_key]);
    }
  });
  return totalScore;
};

/**
 * Helper to calculate the sum of a given set of questions that accepts the parsed jsonResults
 * Sometimes, due to legacy versions, the question keys may not be there. return undefined in this case
 */
export const sumKeysOfJsonResultsObject = (
  jsonResults: string,
  questionKeys: string[],
): number | undefined => {
  const parsedJson = JSON.parse(jsonResults);
  let totalScore = 0;
  for (let i = 0; i < questionKeys.length; i++) {
    const qKey = questionKeys[i];
    if (
      parsedJson[qKey] === undefined ||
      parsedJson[qKey] === null ||
      Number.isNaN(Number.parseInt(parsedJson[qKey], 10))
    ) {
      return undefined;
    }
    totalScore += Number.parseInt(parsedJson[qKey], 10);
  }
  return totalScore;
};

/** Returns true if the survey is less than 3 months old */
const shouldShowAlertDefault = (
  _surveyData: SurveyInstanceData[],
  updatedDate: string,
): boolean => {
  return updatedDate > DateTime.now().minus({ months: 3 }).toISO();
};

/** Returns true if any one of the last four instances has to be alerted*/
const shouldShowAlertPhq9 = (surveyData: SurveyInstanceData[]): boolean => {
  if (surveyData.length < 4) return true;
  const lastFourInstances = surveyData.slice(
    Math.max(surveyData.length - 4, 0), // Getting the last four instances
  );
  return lastFourInstances.some(({ toBeAlerted }) => toBeAlerted === true);
};

const getAlertCondition = (surveyName: string) => {
  if (surveyName === SurveyName.PHQ8) {
    return shouldShowAlertPhq9;
  }
  return shouldShowAlertDefault;
};

/**Validates the responses to detect if there are any responses that has to be alerted */
const isAlertResponseDetected = (
  alertQuestionsDetail: { [key: number]: [string[] | boolean[]] },
  parsedJson: { [key: string]: any },
): boolean => {
  return Object.keys(alertQuestionsDetail ?? {}).some(
    (questionKey: string): boolean => {
      const receivedResponse = parsedJson?.[questionKey];
      const expectedResponse = alertQuestionsDetail[questionKey];
      if (
        Array.isArray(receivedResponse) &&
        Object.keys(expectedResponse).length > 0
      ) {
        return receivedResponse.some((eachSubQuestion) => {
          return Object.keys(eachSubQuestion).some((eachQuestionTitle) => {
            return (
              Object.keys(expectedResponse).includes(
                eachQuestionTitle.toLowerCase(),
              ) &&
              eachSubQuestion[eachQuestionTitle] ===
                expectedResponse[eachQuestionTitle.toLowerCase()]
            );
          });
        });
      }
      if (
        Array.isArray(expectedResponse) &&
        ['string', 'boolean'].includes(typeof receivedResponse)
      ) {
        return expectedResponse.includes(receivedResponse);
      }
      return false;
    },
  );
};

const checkIfSurveyInstanceToBeAlerted = (
  surveyName: string,
  jsonResults: string,
  surveyData: SurveyInstanceData[],
  updatedDate: string,
): boolean => {
  const parsedJson = util.parseJson(jsonResults);
  const alertQuestionsDetail = SurveyNameToAlertQuestionsMap[surveyName];

  if (isEmpty(parsedJson) || isEmpty(alertQuestionsDetail)) {
    return false;
  }

  if (isAlertResponseDetected(alertQuestionsDetail, parsedJson)) {
    return getAlertCondition(surveyName)(surveyData, updatedDate);
  }
  return false;
};

const getKeyValFromCondition = (condition: string, splitter: Splitters) => {
  const [key, expectedVal] = condition.split(splitter);
  return {
    expectedVal: expectedVal?.trim()?.replace(/[{()}]/g, ''),
    key: key?.trim()?.replace(/[{()}]/g, ''),
  };
};

const AGE_VISIBILITY_COND_REGEX = /age\(\{([^}]+)\}\)\s*([><]=?|=)\s*(\d+)/;
const CONTAINS_CONDITIONS_REGEX =
  /{([^}]+)}\s*contains\s*'([^']+)'|{([^}]+)}\s*contains\s*"([^"]+)'/;

/** Returns if a question is visible by checking the condition.
 * For toggle/switch questions the splitter is '=' Eg: {aggression} = true
 * For dropdown questions the splitter is '<>' Eg: {EDtreatment} <> ['none']
 * For Multi select dropdown/Options questions the splitter is 'anyof' Eg: {therapistfamilyhist} anyof ['Social phobia', 'Separation anxiety]
 */
const validateVisibilityCondition = (
  condition: string,
  parsedJson: {
    [key: string]: any;
  },
): boolean => {
  if (isEmpty(condition) || isEmpty(parsedJson)) return null;
  const ageConditionMatches = AGE_VISIBILITY_COND_REGEX.exec(condition);
  const containsConditionMatches = CONTAINS_CONDITIONS_REGEX.exec(condition);
  if (ageConditionMatches) {
    const key = ageConditionMatches[1];
    const comparator = ageConditionMatches[2];
    const value = parseInt(ageConditionMatches[3], 10);
    const dob = parsedJson[key];
    const age = getAgeFromDob(dob);

    switch (comparator) {
      case '>':
        return age > value;
      case '>=':
        return age >= value;
      case '<':
        return age < value;
      case '<=':
        return age <= value;
      case '=':
        return age === value;
      default:
        return false;
    }
  }
  if (containsConditionMatches) {
    const key = containsConditionMatches[1];
    const expectedVal = containsConditionMatches[2];

    const substanceTypes = parsedJson[key];

    if (substanceTypes && Array.isArray(substanceTypes)) {
      return substanceTypes.includes(expectedVal);
    }

    return false;
  }
  if (condition.includes(Splitters.Toggle)) {
    const { expectedVal, key } = getKeyValFromCondition(
      condition,
      Splitters.Toggle,
    );
    let receivedVal = null;

    if (key.includes('.')) {
      // Keys with nested questions Eg: {c-slc-2[0].Since Last Contact} = true
      const [mainKey, subKey] = key.split('.');
      const [formattedKey, index] = mainKey.split('[');

      receivedVal =
        parsedJson?.[formattedKey]?.[Number(index?.replace(']', ''))]?.[subKey];
    } else {
      receivedVal = parsedJson[key];
    }
    return (
      expectedVal.replace(/["']/g, '') ===
      receivedVal
        ?.toString()
        .trim()
        .replace(/[{()}]/g, '')
    );
  }
  if (condition.includes(Splitters.Dropdown)) {
    const { expectedVal, key } = getKeyValFromCondition(
      condition,
      Splitters.Dropdown,
    );

    let expectedArray = expectedVal.replace(/'/g, '"'); //replacing ' with "
    expectedArray = util.parseJson(expectedArray); // Parsing a string array to array

    return intersection(expectedArray, parsedJson[key]).length === 0;
  }
  if (condition.includes(Splitters.MultiSelectDropdown)) {
    const { expectedVal, key } = getKeyValFromCondition(
      condition,
      Splitters.MultiSelectDropdown,
    );
    let expectedArray = expectedVal.replace(/'/g, '"'); //replacing ' with "
    expectedArray = util.parseJson(expectedArray); // Parsing a string array to array

    return intersection(expectedArray, parsedJson[key]).length > 0;
  }

  if (condition.includes(Splitters.GreaterAndEqualTo)) {
    const { expectedVal, key } = getKeyValFromCondition(
      condition,
      Splitters.GreaterAndEqualTo,
    );

    return (
      parseInt(String(parsedJson[key])?.trim(), 10) >= parseInt(expectedVal, 10)
    );
  }
  if (condition.includes(Splitters.LesserAndEqualTo)) {
    const { expectedVal, key } = getKeyValFromCondition(
      condition,
      Splitters.LesserAndEqualTo,
    );
    return (
      parseInt(String(parsedJson[key])?.trim(), 10) <= parseInt(expectedVal, 10)
    );
  }

  if (condition.includes(Splitters.GreaterThan)) {
    const { expectedVal, key } = getKeyValFromCondition(
      condition,
      Splitters.GreaterThan,
    );

    return (
      parseInt(String(parsedJson[key])?.trim(), 10) > parseInt(expectedVal, 10)
    );
  }
  if (condition.includes(Splitters.LesserThan)) {
    const { expectedVal, key } = getKeyValFromCondition(
      condition,
      Splitters.LesserThan,
    );
    return (
      parseInt(String(parsedJson[key])?.trim(), 10) < parseInt(expectedVal, 10)
    );
  }

  if (condition.includes(Splitters.NotEmpty)) {
    const key = condition.split(' ')[0].replace(/[{()}]/g, '');
    const value = parsedJson[key];

    return value !== undefined && value !== null && value !== '';
  }

  return null;
};

const checkIfQuestionIsVisible = (
  visibleIfCond: string,
  visible: boolean,
  parsedJson: {
    [key: string]: any;
  },
): boolean => {
  if (!visibleIfCond && visible === false) return false; //If condition is not present and the question's visibility is marked false then the question is not displayed
  if (!visibleIfCond) return true; //If condition is not present the question is visible
  const orRegexExp = /\s+or\s+(?=(?:[^']*'[^']*')*(?![^']*'))/gi; // Regular expression to match 'or' while avoiding splitting within conditions
  const conditions = visibleIfCond.split(orRegexExp);
  return conditions.some((eachCondition: string) => {
    if (eachCondition.toLowerCase().includes(Splitters.AND)) {
      const andConditions = eachCondition.split(new RegExp(Splitters.AND, 'i'));
      return andConditions.every((ele) =>
        validateVisibilityCondition(ele.trim(), parsedJson),
      );
    }
    return validateVisibilityCondition(eachCondition.trim(), parsedJson);
  });
};

const getAvgScoreOfQuestions = (
  jsonResults: string,
  keys: string[],
): number => {
  if (!keys?.length) return null;
  const parsedJsonRes = util.parseJson(jsonResults);

  const totalScore = keys.reduce(
    (acc, curr) => acc + Number(parsedJsonRes[curr]),
    0,
  );
  return Number.isNaN(totalScore) ? null : round(totalScore / keys.length, 2);
};

/** Helper to calculate the completion percentage of a survey.
  Formula used to calculate completion percentage is
  (Total number of filled answers/total questions * 100) */
const getSurveyCompletionPercentage = (
  surveyDefinition: string,
  jsonResults: string,
): number => {
  const parsedSurveyDef: SurveyDefinition = util.parseJson(surveyDefinition);
  const questions: SurveyQuestion[] = getAllQuestions(parsedSurveyDef);
  const parsedJson = util.parseJson(jsonResults);

  if (isEmpty(questions) || isEmpty(parsedJson)) return null;
  const answers: string[] = [];
  const visibleQuestions: SurveyQuestion[] = [];

  for (const question of questions) {
    const { name, visibleIf, visible } = question;
    if (checkIfQuestionIsVisible(visibleIf, visible, parsedJson)) {
      visibleQuestions.push(question);
      const answer = parsedJson?.[name];
      if (answer !== undefined) answers.push(answer);
    }
  }

  const percentage = Math.round(
    (answers.length / visibleQuestions.length) * 100,
  );
  return percentage;
};

const defaultSurveyInstanceData: SurveyInstanceData = {
  completedBy: '-',
  completionPercentage: null,
  createdDate: '-',
  dateDisplayLabel: null,
  externalId: '',
  jsonResults: '',
  score: null,
  status: null,
  submittedDate: '-',
  surveyDefinition: '',
  surveyName: '',
};

const defaultSurveyCardData: SurveyCardData = {
  alertSurveyDetail: null,
  completedSurveys: 0,
  surveys: [],
  title: '',
  totalNumOfSurveys: 0,
};

// @deprecated - we're moving to a new way for alerts!
/** Helper useful in turning the assessmentData response to
 a more simple object with all the required surveys data
 storing only the required info.*/
const getSurveyCardsData = (
  assessmentData: SurveyResponse[],
  surveyCards: string[],
  setAlertedSurveys: Dispatch<SetStateAction<AlertSurveys[]>>,
  alertedSurveys: AlertSurveys[],
): AllSurveyCardsData => {
  const res: AllSurveyCardsData = {};
  const overviewTabAlertInfo = cloneDeep(alertedSurveys);
  assessmentData.forEach((assessment: SurveyResponse) => {
    const {
      createdDate,
      jsonResults,
      responseExternalId,
      status,
      submittedUserId,
      surveyDefinition,
      surveyName,
      toUserFirstName,
      toUserLastName,
      updatedDate,
    } = assessment;

    if (!jsonResults || !surveyDefinition) {
      console.log(
        'Error: Assessment jsonResults and or surveyDefinition is returned as null',
        { assessment },
      );
    }
    if (surveyCards.includes(surveyName)) {
      const temp: SurveyInstanceData = {
        ...defaultSurveyInstanceData,
        dateDisplayLabel: DateTime.fromISO(updatedDate).toFormat('LL/dd'),
        externalId: responseExternalId,
        jsonResults,
        surveyDefinition,
        surveyName,
      };

      const key = SurveyNameToSurveyKeyMap[surveyName];
      res[key] ??= cloneDeep(defaultSurveyCardData);
      const toCalculateScore = SurveysWithScore.includes(surveyName);
      temp.completionPercentage = getSurveyCompletionPercentage(
        surveyDefinition,
        jsonResults,
      );

      if (SurveysToCheckForAlert.includes(surveyName as SurveyName)) {
        temp.toBeAlerted = checkIfSurveyInstanceToBeAlerted(
          surveyName,
          jsonResults,
          res[key].surveys,
          updatedDate,
        );
        if (temp.toBeAlerted) res[key].alertSurveyDetail = true;
      }

      temp.createdDate = DateTime.fromISO(createdDate)
        .toFormat("LL/dd/yyyy 'at' hh:mma")
        .toLowerCase();
      temp.submittedDate = DateTime.fromISO(updatedDate)
        .toFormat("LL/dd/yyyy 'at' hh:mma")
        .toLowerCase();
      temp.status = SurveyResponseStatusToDisplayStatusMap[status];

      if (status === SurveyResponseStatus.DONE) {
        if (submittedUserId) {
          temp.completedBy = getCompletedByUserName(assessment);
        } else {
          temp.completedBy = 'Patient/support';
        }
        if (toCalculateScore) {
          temp.score =
            surveyName === SurveyName.EDE_Q
              ? getAvgScoreOfQuestions(jsonResults, EdeQSurveyScoreKeys)
              : sumSurveyQuestions(
                  jsonResults,
                  key,
                  QuestionsKeysToExcludeOnScoreCalculation[surveyName],
                );
        }
        res[key].completedSurveys += 1;
      }
      res[key].title = SurveyNameToDisplayNameMap[surveyName];
      if (
        [SurveyResponseStatus.DONE, SurveyResponseStatus.INCOMPLETE].includes(
          status as SurveyResponseStatus,
        )
      ) {
        res[key].totalNumOfSurveys += 1;
      }
      res[key].surveys.push(temp);

      const { completionPercentage: lastCompPercentage, score: lastScore } =
        res[key].surveys[0];

      if (toCalculateScore) {
        res[key].lastScore = lastScore;
      } else {
        res[key].lastCompletionPercentage = lastCompPercentage;
      }
      const surveyIndex = overviewTabAlertInfo.findIndex(
        (e) => e.name === surveyName,
      );

      if (res[key].alertSurveyDetail) {
        if (surveyIndex < 0) {
          overviewTabAlertInfo.push({
            name: surveyName,
            updatedDate,
          } as AlertSurveys);
        }
      } else if (surveyIndex >= 0) {
        overviewTabAlertInfo.splice(surveyIndex, 1);
      }
      temp.assignedTo = `${toUserFirstName} ${toUserLastName}`;
    }
  });
  setAlertedSurveys(overviewTabAlertInfo);
  return res;
};

const getPercentageDisplayLabel = (
  percentage: number,
  status: AssessmentDisplayStatus,
  prefix = '',
): string => {
  if (
    (status === AssessmentDisplayStatus.COMPLETE ||
      status === AssessmentDisplayStatus.INCOMPLETE) &&
    percentage
  ) {
    return prefix ? `${prefix} ${percentage}%` : `${percentage}%`;
  }
  return '-';
};

const isSurveyRestrictedByDiagnosis = (
  jsonParameters: string,
  patientDiagnoses: PatientDiagnosis[],
): boolean => {
  const parsedParams = JSON.parse(jsonParameters);
  const { excludedDiagnosis, restrictToDiagnoses } = parsedParams;

  const openDiagnosesCodes = patientDiagnoses
    .filter((diagnosis) =>
      isOpenDiagnosis(diagnosis.statusCode as DiagnosisStatusCode),
    )
    .map((diagnosis) => diagnosis.diagnosis.code);

  if (Array.isArray(restrictToDiagnoses) && restrictToDiagnoses.length > 0) {
    return !restrictToDiagnoses.some((rtd) => openDiagnosesCodes.includes(rtd));
  }
  if (Array.isArray(excludedDiagnosis)) {
    return excludedDiagnosis.some((ed) => openDiagnosesCodes.includes(ed));
  }
  return false;
};

type GeneratePDFParams = {
  endDate: string;
  jsonResults: Record<string, any>;
  patientName: string;
  surveyDefinition: SurveyDefinition;
  surveyName: string;
};

const generatePDF = ({
  endDate,
  jsonResults,
  patientName,
  surveyDefinition,
  surveyName,
}: GeneratePDFParams): void => {
  const pdf = new JSPDF();
  pdf.setFont(fontFace, 'normal');
  pdf.setFontSize(fontSize);
  pdf.setLineHeightFactor(0.8);

  const lineHeight = pdf.getLineHeight();
  const maxLineWidth = pageWidth - margin * 3;

  // Title
  pdf.text(
    `${surveyName} Survey`,
    margin + pdfOffsets.title.x,
    margin + pdfOffsets.title.y,
  );

  // Patient name
  pdf.text(
    patientName,
    margin + pdfOffsets.patientName.x,
    margin + pdfOffsets.patientName.y,
  );

  // Completed date
  pdf.text(
    !endDate
      ? ''
      : `Completed: ${DateTime.fromISO(endDate).toFormat('LL/dd/yyyy')}`,
    margin + pdfOffsets.completed.x,
    margin + pdfOffsets.completed.y,
  );

  // Logo
  pdf.addImage(
    logo,
    'JPEG',
    margin + pdfOffsets.logo.x,
    margin + pdfOffsets.logo.y,
    0,
    0,
    'Equip logo',
  );

  let yOffset = pdfOffsets.resultsContainer.y;
  const questions = getAllQuestions(surveyDefinition);
  const questionsToExclude = getExcludedQuestionKeys(surveyName);
  const resolveTitle = buildResolvedTitleLookup(surveyDefinition, jsonResults);

  for (const question of questions) {
    const { type, name, html, visibleIf, visible, title } = question;
    let text = title && resolveTitle(name);

    if (
      !questionsToExclude.includes(name) &&
      checkIfQuestionIsVisible(visibleIf, visible, jsonResults)
    ) {
      if (html) {
        text = html
          .replace(/&nbsp;/g, ' ') // replace html non-breaking space escape character with a space
          .replace(/&#8226;/g, ' - ') // replace html bullet escape character with a dash
          .replace(/&#8220;|&#8221;/g, '"') // replace double quote escape characters with double quotes
          .replace(/<br>/g, '\n') // replace <br> html tag with new line escape character
          .replace(/<[^>]*>/g, '') // remove all other html tags, e.g. <li>
          .replace(/\\n/g, ''); // remove all newline characters represented as escaped characters
      }

      const questionLines = pdf.splitTextToSize(text, maxLineWidth);
      const questionLinesHeight =
        (questionLines.length * fontSize * lineHeight) / ptsPerInch;
      const answer = jsonResults[name];
      const answerComment = jsonResults[`${name}-Comment`];
      const answerLines = pdf.splitTextToSize(
        getAnswerTitle(questions, name, answer, answerComment)
          .map((answerText: string) => removeUrlsFromText(answerText, '[Link]'))
          .join('\n\n'),
        maxLineWidth,
      );
      answerLines[0] = parseBooleanText(answerLines[0]);
      const answerLinesHeight =
        ((answerLines.length /
          (Array.isArray(answer) && answer.length > 1 ? 2 : 1)) *
          fontSize *
          lineHeight) /
        ptsPerInch;

      if (yOffset + questionLinesHeight + answerLinesHeight >= pageHeight) {
        pdf.addPage();
        yOffset = 20;
      }
      pdf.setFont(fontFace, 'bold');
      pdf.text(questionLines, margin, yOffset, { lineHeightFactor: 2 });
      if (type === 'matrixdropdown') {
        Object.keys(answer).forEach((eachSubQuestion) => {
          yOffset += 5;
          pdf.setFont(fontFace, 'bold');
          pdf.text(eachSubQuestion, margin + 5, yOffset, {
            lineHeightFactor: 2,
          });

          Object.values(answer[eachSubQuestion]).forEach((ans: string) => {
            pdf.setFont(fontFace, 'normal');
            pdf.text(
              ans?.toString(),
              (eachSubQuestion.length + 5) * 3,
              yOffset,
            );
          });
        });
      } else {
        yOffset += questionLinesHeight;
        pdf.setFont(fontFace, 'normal');
        pdf.text(answerLines, margin + pdfOffsets.answer.x, yOffset);
        yOffset += answerLinesHeight;
      }
    }
  }

  pdf.save(`${patientName ?? ''}_${surveyName ?? ''}_Survey`);
};

const stringToJson = (data: string): any =>
  typeof data === 'string' ? util.parseJson(data) : null;

const shouldShowEditOption = (
  email: string,
  status: SurveyResponseStatus,
): boolean =>
  !!email &&
  [
    SurveyResponseStatus.DONE,
    SurveyResponseStatus.IN_PROGRESS,
    SurveyResponseStatus.INCOMPLETE,
    SurveyResponseStatus.NEW,
    SurveyResponseStatus.REQUESTED,
  ].includes(status);

const mapSurvey = (
  maptoJson: any,
  mapfromJson: any,
  mapping: Array<SurveyMapping>,
  mappingValues: any,
): any => {
  const {
    patientState,
    patientZipcode,
    patientCity,
    intake_pt_mail_line_2,
    patientMail,
  } = maptoJson;
  const surveyAddress: Partial<Patient> = {
    ...(patientMail ? { addressLine1: patientMail } : {}),
    ...(intake_pt_mail_line_2 ? { addressLine2: intake_pt_mail_line_2 } : {}),
    ...(patientState ? { state: patientState } : {}),
    ...(patientZipcode ? { zip: patientZipcode } : {}),
    ...(patientCity ? { city: patientCity } : {}),
  };
  const { addressLine1, addressLine2, zip, city, state } = mapfromJson;
  const basicAddressWithoutState: Partial<Patient> = {
    addressLine1,
    addressLine2,
    zip,
    city,
  };
  let appendSurveyResult: AppendSurveyMappingItems = {};

  // Check if survey address has values and basic address has empty fields except for state
  const dbHasOnlyState =
    !!state && !addressLine1 && !addressLine2 && !city && !zip;

  const shouldMapWholeSurveyAddress: boolean =
    (dbHasOnlyState ||
      Object.values(basicAddressWithoutState).every((value) => !value)) &&
    Object.values(surveyAddress).some((value) => value);

  mapping.forEach((map) => {
    let mappedValue = null;

    const filterMappingValues = mappingValues.filter((mappingValue) => {
      return mappingValue.field === map.key;
    });
    if (map.type === 'array') {
      if (
        isEmptyArray(mapfromJson[map.key]) &&
        maptoJson[map.value] &&
        !isEmptyArray(maptoJson[map.value])
      ) {
        let maptoJsonValuesArray = [];
        if (Array.isArray(maptoJson[map.value])) {
          maptoJsonValuesArray = maptoJson[map.value];
        } else {
          maptoJsonValuesArray = [maptoJson[map.value]];
        }

        if (filterMappingValues) {
          mappedValue = maptoJsonValuesArray.map(
            (m2JValue) =>
              filterMappingValues.find(({ value }) => {
                return m2JValue === value;
              })?.key ?? m2JValue,
          );
        }
      }
    } else if (!mapfromJson[map.key] && maptoJson[map.value]) {
      const surveyProperyValue = filterMappingValues.find(
        (patientSurveyMappingValue) => {
          if (
            maptoJson.intake_is_pt_trans &&
            patientSurveyMappingValue.field === 'genderOnRecord' &&
            [
              'Cis-gender male',
              'Cis-gender female',
              'Transgender male',
              'Transgender female',
            ].includes(patientSurveyMappingValue.key)
          ) {
            return (
              patientSurveyMappingValue.value ===
              `${maptoJson[map.value]}-Trans`
            );
          }
          return patientSurveyMappingValue.value === maptoJson[map.value];
        },
      );
      mappedValue = surveyProperyValue?.key ?? maptoJson[map.value];
    }
    if (mappedValue) {
      appendSurveyResult = {
        ...appendSurveyResult,
        [map.key]: mappedValue,
      };
    }
  });
  /** Replacing basic info address  with survey address if all patient address fields except state are empty*/
  return shouldMapWholeSurveyAddress === true
    ? { ...appendSurveyResult, ...surveyAddress }
    : appendSurveyResult;
};

/** Extracts the diagnoses related questions from survey and returns its response */
const getDiagnosesQuestionsResponseFromIntakeSurvey = (
  assessmentList: SurveyResponse[],
): string[] => {
  const admissionsIntake = assessmentList?.find(
    ({ status, surveyName: _surveyName }: SurveyResponse) =>
      _surveyName === SurveyName.ADMISSIONS_INTAKE &&
      status === SurveyResponseStatus.DONE,
  );
  const surveyDiagnosisList = [];

  if (admissionsIntake) {
    const { surveyName, jsonResults } = admissionsIntake;
    const parsedJson = stringToJson(jsonResults);
    const questionKeys = SurveyDiagnosesToDiagnosesQuestionKeysMap[surveyName];

    questionKeys.forEach((key: string) => {
      const answer = parsedJson?.[key];

      if (Array.isArray(answer)) {
        answer.forEach((eachAnswer) => {
          if (isObject(eachAnswer)) {
            surveyDiagnosisList.push(Object.values(eachAnswer)[0]);
          } else {
            surveyDiagnosisList.push(eachAnswer);
          }
        });
      } else {
        surveyDiagnosisList.push(answer);
      }
    });
  }

  return surveyDiagnosisList;
};

const buildResolvedTitleLookup = (
  definition: SurveyDefinition,
  data: Record<string, unknown>,
): ((questionName: string) => string | undefined) => {
  const model = new Model(definition);
  model.data = data;
  return (questionName: string) =>
    model.getQuestionByName(questionName).locTitle.renderedHtml;
};

const parseAssessmentResults = (assessment: SurveyResponse) => {
  const {
    surveyDefinition,
    jsonResults,
    responseExternalId: externalId,
  } = assessment;
  const parsedData: SurveyDefinition = util.parseJson(surveyDefinition);
  const parsedJson = util.parseJson(jsonResults);
  const questions: SurveyQuestion[] = getAllQuestions(parsedData);
  const resolveTitle = buildResolvedTitleLookup(parsedData, parsedJson);
  const questionResult = {};

  questions.forEach((question) => {
    const {
      choices,
      columns,
      html,
      isRequired,
      name: questionKey,
      title,
      type,
      visible,
      visibleIf,
    } = question;
    const resolvedTitle = title && resolveTitle(questionKey);
    if (checkIfQuestionIsVisible(visibleIf, visible, parsedJson)) {
      const answer = parsedJson[questionKey];
      const answerComment =
        parsedJson[`${questionKey}-Comment`] ||
        parsedJson[`${questionKey}:-Comment`];
      let answerLine = getAnswerTitle(
        questions,
        questionKey,
        answer,
        answerComment,
      )
        .map((answerText: string) => removeUrlsFromText(answerText, '[Link]'))
        .join('\n\n');

      answerLine = parseBooleanText(answerLine);
      questionResult[questionKey] = questionResult[questionKey] || [];

      while (questionResult[questionKey].length < 0) {
        questionResult[questionKey].push({
          answerLine: null,
          choices: null,
          columns,
          externalId: null,
          questionLine: resolvedTitle,
          type,
          value: null,
        });
      }

      questionResult[questionKey].push({
        answerLine,
        choices,
        columns,
        externalId,
        html,
        isRequired,
        questionLine: resolvedTitle,
        type,
        value: answer,
      });
    }
  });
  return questionResult;
};

const getRespondentNameForSurveyPdf = (
  surveyName: string,
  patientPreferredName: string,
  toUserFirstName: string,
  toUserLastName: string,
  submittedUserDisplayFirstName?: string,
  submittedUserDisplayLastName?: string,
  submittedUserName?: string,
): string => {
  if (ClinicianAssessmentsSurveyCards.includes(surveyName)) {
    return patientPreferredName;
  }
  if (submittedUserDisplayFirstName && submittedUserDisplayLastName) {
    return `${submittedUserDisplayFirstName} ${submittedUserDisplayLastName}`;
  }
  if (submittedUserName) {
    return submittedUserName;
  }
  return `${toUserFirstName} ${toUserLastName}`;
};

export {
  buildResolvedTitleLookup,
  checkIfQuestionIsVisible,
  ClinicianAssessmentsSurveyCards,
  generatePDF,
  getAllQuestions,
  getAnswerTitle,
  getAvgScoreOfQuestions,
  getDiagnosesQuestionsResponseFromIntakeSurvey,
  getRespondentNameForSurveyPdf,
  getPercentageDisplayLabel,
  sumSpecificKeysLegacy,
  sumSpecificKeys,
  getSurveyCardsData,
  getSurveyCompletionPercentage,
  sumSurveyQuestions,
  isAlertResponseDetected,
  isSurveyRestrictedByDiagnosis,
  mapSurvey,
  parseBooleanText,
  parseObject,
  parseTelephone,
  parseText,
  shouldShowAlertDefault,
  shouldShowAlertPhq9,
  shouldShowEditOption,
  stringToJson,
  validateVisibilityCondition,
  parseAssessmentResults,
};
